<script setup lang="ts">
import BookCover from '@/components/bookshelf/BookCover.vue';
import { computed, ref, watch } from 'vue';
import { BookWithDuplicates } from '@ilteducation/books';
import type { BookV1 } from '@ilteducation/content-registry-types';
import BookPreview from '@/components/bookshelf/BookPreview.vue';
import { useRoute } from 'vue-router';
import { useLocale } from '@/plugins/translation';
import { useDialogFocus } from '@ilteducation/components';
import {
  createSearchId,
  SearchQuery,
  useContentByOriginalTitle,
  useSearchContexts,
} from '@ilteducation/content-search';
import { storeToRefs } from 'pinia';
import { useUserContext } from '@ilteducation/ilt-auth';
import { AvailabilityRealmsFilter } from '@ilteducation/content-search/src/content-filters';
import { escapeSearchSpecialCharacters } from '@ilteducation/content-search/src/query-formatters';
import { BookWithRelatedContents } from '@ilteducation/videos';
import { useDocumentInfiniteScroll } from '@/composables/use-document-infinite-scroll';

const emits = defineEmits<{
  (e: 'load-more'): void;
}>();

const props = defineProps<{
  books: BookWithDuplicates[];
  isLoading: boolean;
  hasMore: boolean;
}>();

const selectedBook = ref<BookWithDuplicates | null>(null);
const previewDialog = ref(false);
const { rememberFocus } = useDialogFocus([previewDialog]);

const { t, l } = useLocale();

const searchContextsStore = useSearchContexts<BookV1>('Book');
const { search, clearSearch } = searchContextsStore;
const { result } = storeToRefs(searchContextsStore);

const userContext = useUserContext();
const { realm } = storeToRefs(userContext);
const globalFilters = computed(() => AvailabilityRealmsFilter(realm.value));
const book = computed(() => selectedBook.value);
const booksWithSameOriginalTitle = computed(() => {
  return book.value ? useContentByOriginalTitle(ref(book.value), 'Book', globalFilters).value : [];
});

const sqSwedish = computed<SearchQuery>(() => ({
  filter: {
    title: escapeSearchSpecialCharacters(selectedBook.value?.originalTitle || ''),
    isbn: String(selectedBook.value?.isbn),
  },
}));
const swedishBook = computed(() => (result.value(createSearchId('Book', sqSwedish.value)) || []) as BookV1[]);

watch(
  book,
  async (newBook) => {
    if (!newBook) {
      clearSearch();
    } else {
      search(sqSwedish.value, 1);
    }
  },
  { immediate: true },
);

const allBooks = computed(() => {
  const books = booksWithSameOriginalTitle.value;
  if (swedishBook.value.length) {
    return Array.from(
      new Map([...books, ...swedishBook.value].map((b) => [b.id, b])).values(),
    ) as BookWithRelatedContents[];
  }
  return books as BookWithRelatedContents[];
});

const handleBookSelection = (book: BookV1) => {
  rememberFocus(); //Capture focus of the active element
  selectedBook.value = book;
  previewDialog.value = true;
};

const route = useRoute();
const preferredLanguage = computed(() => route.query['languages.secondaryLanguage'] as string);

// Infinite scroll detection
useDocumentInfiniteScroll({
  isLoading: computed(() => props.isLoading),
  hasMore: computed(() => props.hasMore),
  itemCount: computed(() => props.books.length),
  onLoadMore: () => emits('load-more'),
});
</script>

<template>
  <div>
    <v-row justify="center" class="gc-sm-6 gc-md-12 gr-4 my-6">
      <v-col v-for="book in books" :key="book.id" cols="auto">
        <BookCover
          :show-as-stack="!!book.duplicatesCount"
          class="book-cover cursor-pointer"
          :book="book"
          @on-book-click="handleBookSelection"
        ></BookCover>
      </v-col>
    </v-row>
    <div v-if="isLoading" class="d-flex justify-center my-6">
      <v-progress-circular indeterminate size="64" color="primary" :aria-label="t(l.label_loading_books)" />
    </div>
    <BookPreview
      v-if="allBooks.length"
      v-model="previewDialog"
      :books="allBooks"
      :preferred-language="preferredLanguage"
    />
  </div>
</template>

<style scoped lang="scss"></style>
